import React from 'react';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { Badge } from 'primereact/badge';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/appheader.css';
import { useAuth } from '../hooks/useAuth';
import type { Client } from '../features/dashboard/types/dashboard';
import SelectClient from '../components/SelectClient';
import { useSelectedClient } from '../hooks/useSelectedClient';
import '../styles/layout.css';

const farmersLogo = 'https://landing-cdn.nowcerts.com/client-application/release/assets/images/intro/nowcerts-insurance-agency-management-system-identity-dark.png';

export type HeaderBarProps = {
  onToggleNav: () => void;
  appName?: string;
  onClientSelected?: (client: Client) => void;
  selectedClientName?: string; 
};
export default function HeaderBar({ onToggleNav, appName = 'MomentumAMP', onClientSelected, selectedClientName }: HeaderBarProps) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { clearClient, insuredId } = useSelectedClient();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const profileRef = React.useRef<HTMLDivElement | null>(null);
  const notifBtnRef = React.useRef<HTMLDivElement | null>(null);
  const [clientQuery, setClientQuery] = React.useState('');
  const [notifOpen, setNotifOpen] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState<number>(0);
  const [activity, setActivity] = React.useState<any[]>([]);
  const [activityLoading, setActivityLoading] = React.useState(false);

  const displayEmail = user?.email ?? 'guest@momentumamp.com';
  const firstName = localStorage.getItem('firstName') ?? '';
  const lastName = localStorage.getItem('lastName') ?? '';
  const displayName = [firstName, lastName].filter(Boolean).join(' ') || displayEmail;
  const avatarLabel = (firstName?.[0] ?? user?.email?.[0] ?? 'U').toUpperCase();

  const handleLogout = () => {
    clearClient();
    logout();
    navigate('/');
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  React.useEffect(() => {
    if (!notifOpen) return;
    const handleNotifClickOutside = (event: MouseEvent) => {
      if (notifBtnRef.current && !notifBtnRef.current.contains(event.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleNotifClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleNotifClickOutside);
    };
  }, [notifOpen]);

  // selectedClientName is intentionally not synced to the search input
  // so it always shows the placeholder after a client is selected

  React.useEffect(() => {
    const fetchUnread = async () => {
      try {
        const token = localStorage.getItem('igtoken');
        if (!token) return;
        const params = new URLSearchParams();
        if (insuredId) params.set('insuredDatabaseId', insuredId);
        const res = await axios.get(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/activity-unread-count?${params}`, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        setUnreadCount(res.data.unreadCount || 0);
      } catch {
        setUnreadCount(0);
      }
    };
    fetchUnread();
  }, [insuredId]);

  React.useEffect(() => {
    if (!notifOpen) return;
    const fetchActivity = async () => {
      setActivityLoading(true);
      try {
        const token = localStorage.getItem('igtoken');
        if (!token) return;
        const params = new URLSearchParams({ hours: '2160' });
        if (insuredId) params.set('insuredDatabaseId', insuredId);
        const res = await axios.get(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/recent-activity?${params}`, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        setActivity(Array.isArray(res.data) ? res.data : []);
      } catch {
        setActivity([]);
      } finally {
        setActivityLoading(false);
      }
    };
    fetchActivity();
  }, [notifOpen]);

  const handleMarkAsRead = async (id: number) => {
    try {
      const token = localStorage.getItem('igtoken');
      if (!token) return;
      await axios.post(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/activity/${id}/read`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setActivity((prev) => prev.map((a) => a.id === id ? { ...a, isRead: true } : a));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {}
  };

  return (
    <header className="app-header" role="banner">
      <div className="app-header__left">
        <Button
          type="button"
          icon="pi pi-bars"
          className="p-button-text p-button-plain app-header__iconBtn"
          onClick={onToggleNav}
          aria-label="Toggle navigation"
        />
        <div className="app-header__brand" aria-label={appName}>
          <img src={farmersLogo} alt={appName} className="app-header__brandLogo" />
        </div>
      </div>

      <div className="app-header__center" role="search">
        <span className="app-header__search">
          <i className="pi pi-search app-header__searchIcon" />
          <SelectClient
            value={clientQuery}
            onChange={setClientQuery}
            onClientSelected={onClientSelected as (client: Client) => void}
          />
        </span>
      </div>

      <div className="app-header__right" aria-label="Header actions">
        {/* <div
          className="app-header__badgeBtn"
          style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
          ref={notifBtnRef}
        >
          <button
            type="button"
            className="p-button-text p-button-plain app-header__iconBtn"
            aria-label="Notifications"
            style={{ position: 'relative', background: 'none', border: 'none', padding: 0, margin: 0, cursor: 'pointer' }}
            onClick={e => {
              e.stopPropagation();
              setNotifOpen((open) => !open);
            }}
          >
            <i className="pi pi-bell" style={{ fontSize: 18, color: '#444' }} />
            {unreadCount > 0 && (
              <span className="app-header__notifBadge">{unreadCount}</span>
            )}
          </button>
          {notifOpen && (
            <div className="app-header__notifDropdown">
              <div className="app-header__notifDropdownTitle" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Notifications</span>
                <Button
                  type="button"
                  icon="pi pi-times"
                  className="p-button-text p-button-plain"
                  aria-label="Close notifications"
                  onClick={() => setNotifOpen(false)}
                  style={{ fontSize: 18, marginLeft: 8 }}
                />
              </div>
              {activityLoading ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>
                  <ProgressSpinner style={{ width: 32, height: 32 }} strokeWidth="4" />
                </div>
              ) : activity.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>No recent activity.</div>
              ) : (
                <div className="app-header__notifList">
                  {activity.filter(a => !a.isRead).map((a) => (
                    <div
                      key={a.id}
                      className="app-header__notifChip app-header__notifChip--compact"
                    >
                      <div className="app-header__notifTitle app-header__notifTitle--compact">{a.title}</div>
                      <div className="app-header__notifDesc app-header__notifDesc--compact">
                        {a.description.replace(/certificate [a-f0-9\-]+/i, 'Certificate')}
                      </div>
                      <div className="app-header__notifTime app-header__notifTime--compact">{a.timeAgo}</div>
                      <button
                        className="app-header__notifMarkBtn app-header__notifMarkBtn--compact"
                        onClick={() => handleMarkAsRead(a.id)}
                        title="Mark as read"
                      >
                        <i className="pi pi-check" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div> */}
        {/* <Button
          type="button"
          icon="pi pi-cog"
          className="p-button-text p-button-plain app-header__iconBtn"
          aria-label="Settings"
        /> */}
        <div className="app-header__profileWrapper" ref={profileRef}>
          <button
            type="button"
            className="app-header__profile"
            aria-label="Profile menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <Avatar label={avatarLabel} shape="circle" />
            <span className="app-header__profileName">{displayName}</span>
            <i className={menuOpen ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} aria-hidden="true" />
          </button>
          {menuOpen && (
            <div className="app-header__profileMenu" role="menu">
              <div className="app-header__profileMeta">
                <div className="app-header__profileMetaName">{displayName}</div>
                {/* <div className="app-header__profileMetaRole">Signed in</div> */}
              </div>
              {/* <button type="button" className="app-header__profileMenuItem" role="menuitem">
                <i className="pi pi-user" aria-hidden="true" />
                <span>Profile</span>
              </button> */}
              <button
                type="button"
                className="app-header__profileMenuItem"
                role="menuitem"
                onClick={handleLogout}
              >
                <i className="pi pi-sign-out" aria-hidden="true" />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
